import {use, useCallback, useMemo, useRef, useState} from 'react'
import {Comments} from '../comments/Comments'
import {CommentsContext, ViewMode} from '../comments/CommentsContext'
import {CommentsViewControls} from '../comments/CommentsViewControls'
import {
  IssueCommentComposer,
  isIssueCommentComposerEnabled,
  type IssueCommentComposerHandle,
} from '../comments/IssueCommentComposer'
import {useFilteredCommentsData} from '../comments/use-filtered-comments-data'
import {useLayoutAuthorLogin, useLayoutCanComment} from '../../stores/layout-selectors'
import {usePullRequestBasePath} from '../../stores/selectors'
import styles from './CommentsSection.module.css'
import {Stack} from '@primer/react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useCommentsFilterState} from '../comments/use-comments-filter-state'

export function CommentsSection() {
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.IssueComments)

  const commentsContextValue = useMemo(() => ({viewMode, setViewMode}), [viewMode, setViewMode])

  return (
    <section>
      <h2 className={styles.heading}>Comments</h2>
      <ErrorBoundary
        // TODO: Improve error fallback
        fallback="Failed to load comments. Please refresh the page to try again."
        boundaryName="OverviewCommentsSection"
      >
        <CommentsContext value={commentsContextValue}>
          <CommentsSectionContent />
        </CommentsContext>
      </ErrorBoundary>
    </section>
  )
}

function CommentsSectionContent() {
  const pathName = usePullRequestBasePath()
  const pullRequestAuthorLogin = useLayoutAuthorLogin()

  const viewMode = use(CommentsContext).viewMode
  const {filterState, setCurrentFilterState} = useCommentsFilterState(viewMode)
  const {authorList, filteredConversationItems, selectedReviewId, totalConversationItemsCount, isReviewCommentsMode} =
    useFilteredCommentsData({filterState})

  const composerRef = useRef<IssueCommentComposerHandle>(null)
  const handleQuoteReply = useCallback((quotedText: string) => composerRef.current?.insertQuote(quotedText), [])

  const isComposerEnabled = isIssueCommentComposerEnabled(isReviewCommentsMode, useLayoutCanComment())

  return (
    <Stack direction="vertical" gap="normal">
      <CommentsViewControls authorList={authorList} filterState={filterState} setFilterState={setCurrentFilterState} />
      <Comments
        filteredConversationItems={filteredConversationItems}
        selectedReviewId={selectedReviewId}
        handleQuoteReply={handleQuoteReply}
        pathName={pathName}
        pullRequestAuthorLogin={pullRequestAuthorLogin}
        totalConversationItemsCount={totalConversationItemsCount}
        isReviewCommentsMode={isReviewCommentsMode}
      />
      {/* Collapsed rather than unmounted so an in-progress draft survives a switch to the review view. */}
      <IssueCommentComposer ref={composerRef} collapsed={!isComposerEnabled} />
    </Stack>
  )
}
